import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import SignupForm from "../../components/auth/SignupForm/SignupForm";
import { useAuth } from "../../hooks/useAuth";
import { captureSignupRoleSelected, captureTeenProfilePhotoUploaded, captureUserSignedUp } from "../../lib/analytics";
import { ApiError } from "../../lib/api";
import { mediaApi } from "../../lib/media";
import type { AccountType, SignupRequestDto, SignupRole } from "../../types/types";
import { getDefaultAuthenticatedPath } from "../../utils/AuthUtils";

const SignupPage = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, signup, status, user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inviteEmail = searchParams.get("email")?.trim() ?? "";
  const inviteRoleParam = searchParams.get("role")?.trim().toUpperCase();
  const accountTypeParam = searchParams.get("accountType")?.trim().toUpperCase();
  const inviteRole: SignupRole | "" = inviteRoleParam === "ADULT" || inviteRoleParam === "TEEN" ? inviteRoleParam : "";
  const inviteAccountType: AccountType = accountTypeParam === "BUSINESS" ? "BUSINESS" : "PERSONAL";
  const isGuardianInvite = searchParams.get("guardianInvite") === "1";
  const hasPostJobIntent = inviteRole === "ADULT" && searchParams.get("intent") === "post-job";
  const inviteCopy = useMemo(() => {
    if (!isGuardianInvite || !inviteRole) {
      return null;
    }

    return inviteRole === "ADULT"
      ? "Finish signup with this email to automatically connect as a guardian."
      : "Finish signup with this email to automatically connect with your guardian request.";
  }, [inviteRole, isGuardianInvite]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(getDefaultAuthenticatedPath(user?.roles), { replace: true });
    }
  }, [isAuthenticated, navigate, user?.roles]);

  const handleSignup = async (payload: SignupRequestDto, profilePhotoFile?: File | null) => {
    const postJobIntent = hasPostJobIntent && payload.role === "ADULT";
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const authUser = await signup({ ...payload, postJobIntent });
      captureUserSignedUp(posthog, {
        role: payload.role,
        account_type: payload.accountType ?? "PERSONAL",
        has_guardian_invite: isGuardianInvite,
      });
      if (profilePhotoFile) {
        try {
          await mediaApi.uploadFile(profilePhotoFile, "PROFILE_PHOTO");
          if (payload.role === "TEEN") {
            captureTeenProfilePhotoUploaded(posthog, { source: "signup" });
          }
        } catch {
          // Keep signup non-blocking if the optional photo upload fails.
        }
      }
      const intentQuery = postJobIntent ? "&intent=post-job" : "";
      navigate(`/verify-email/pending?email=${encodeURIComponent(authUser.email)}${intentQuery}`, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setErrorMessage("That email is already registered.");
      } else {
        setErrorMessage("Unable to create account. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === "idle" || status === "loading") {
    return (
      <main className="mx-auto max-w-lg px-6 py-16">
        <p className="text-myteengig-text-muted">Checking your session...</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-lg px-6 py-16">
      <h1 className="mb-6 text-2xl font-bold text-myteengig-text-primary">Sign up</h1>
      {inviteCopy ? (
        <div className="mb-4 rounded-xl border border-myteengig-primary/20 bg-myteengig-secondary px-4 py-3 text-sm text-myteengig-text-primary">
          {inviteCopy}
        </div>
      ) : null}
      <SignupForm
        onSubmit={handleSignup}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        initialEmail={inviteEmail}
        initialRole={inviteRole}
        initialAccountType={inviteAccountType}
        lockEmail={Boolean(isGuardianInvite && inviteEmail)}
        lockRole={Boolean(isGuardianInvite && inviteRole)}
        lockAccountType={Boolean(inviteRole === "ADULT" && inviteAccountType === "BUSINESS")}
        onRoleChange={(role) => captureSignupRoleSelected(posthog, { role, source: "signup_form" })}
      />
      <p className="mt-4 text-sm text-myteengig-text-muted">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-myteengig-primary hover:underline">
          Log in
        </Link>
      </p>
      <p className="mt-2 text-sm text-myteengig-text-muted">
        <Link to="/" className="font-medium text-myteengig-primary hover:underline">
          Back to Home
        </Link>
      </p>
    </main>
  );
};

export default SignupPage;
